import React from 'react';

import { Grid, makeStyles, Typography } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import FilterSelector from 'components/FilterSelector';
import {
  getFlavorsCostRequest,
  setFlavorsCostPeriodFilter,
} from 'modules/monitor/actions';
import {
  PeriodCountAccess,
  ListFlavorsCostFields,
  FlavorsCostFields,
} from 'modules/monitor/interfaces';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import { formatCurrency, formatPercent } from 'utils/helpers';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import { filterPeriodOptions } from 'utils/graphs';
import Button from 'components/Button';
import i18n from 'i18n';
import { useTableFilters } from 'hooks/useTableFilters';
import { SearchBar } from 'components/SearchBar';
import FlavorEnvironments from './FlavorEnvironments';
import styles, { overrideFilter } from './styles';

const useStyles = makeStyles(styles);

const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface FlavorsCostTableProps {
  flavorsCostFields: ListFlavorsCostFields;
  intervalTime: PeriodCountAccess;
  isLoading: boolean;
  getFlavorsCostRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  setFlavorsCostPeriodFilter: (intervalTime: string) => void;
}

const FlavorsCostTable = ({
  flavorsCostFields,
  intervalTime,
  isLoading,
  getFlavorsCostRequest,
  modalClose,
  modalOpen,
  setFlavorsCostPeriodFilter,
}: FlavorsCostTableProps) => {
  const classes = useStyles();
  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters(initialQuery);

  const headers: Array<TableCellHeaderProps> = [
    {
      key: 'flavor',
      i18n: 'Flavor',
      order: true,
      style: { width: '30%' },
    },
    {
      key: 'num_instances',
      i18n: i18n.t('INSTANCES_KEY'),
      order: true,
      centerTitle: true,
      textAlignCenter: true,
    },
    {
      key: 'total_cost',
      i18n: i18n.t('COSTS_PAGE.COST_KEY'),
      order: true,
    },
    {
      key: 'cost_percentage',
      i18n: i18n.t('COSTS_PAGE.COST_PERCENTAGE_KEY'),
      order: true,
    },
    {
      key: 'flavor_details',
      i18n: 'Detalhes',
      centerTitle: true,
    },
  ];

  React.useEffect(() => {
    getFlavorsCostRequest({ ...query, period: intervalTime });
  }, [getFlavorsCostRequest, intervalTime, query]);

  const openDetailsModal = (item: FlavorsCostFields) => {
    modalOpen({
      title: i18n.t('COSTS_PAGE.FLAVOR_DETAILS_KEY'),
      content: <FlavorEnvironments flavorSelected={item} />,
      maxWidth: 'md',
      fullWidth: true,
      footer: (
        <Grid>
          <Button id="close-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const [isSearchLocked, setIsSearchLocked] = React.useState(false);

  const initiateSearch = searchValue => {
    if (isSearchLocked || isLoading) return;
    setIsSearchLocked(true);

    handleSearch(searchValue);

    setTimeout(() => {
      setIsSearchLocked(false);
    }, 150);
  };

  return (
    <Grid item className={classes.flavorsCostContainer}>
      <Grid container className={classes.header}>
        <Grid item className={classes.searchFormContainer}>
          <SearchBar
            id="search-flavor"
            isLoading={isLoading}
            searchValue={searchValue}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>

        <Grid item className={classes.searchButton}>
          <Button
            id="search-flavors-cost"
            variant="outlined"
            onClick={() => {
              initiateSearch(searchValue);
            }}
            disabled={isLoading || isSearchLocked}
          >
            <Typography className={classes.searchButtonText}>
              {i18n.t('SEARCH_ALL_KEY')}
            </Typography>
          </Button>
          <FilterSelector
            overrideClasses={overrideFilter}
            options={filterPeriodOptions(setFlavorsCostPeriodFilter)}
            currentValue={{
              label: i18n.t(
                `ENVIRONMENT_DASHBOARD.LAST_${intervalTime.toUpperCase()}`
              ),
              onClick: () => null,
            }}
          />
        </Grid>
      </Grid>

      <Grid container className={classes.flavorsCostTable}>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  centerTitle={item?.centerTitle}
                  textAlignCenter={item?.textAlignCenter}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  style={item?.style}
                >
                  {item?.i18n}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: FlavorsCostFields) => (
            <TableRow>
              <TableCell>{getFlavorDisplayName(item?.flavor)}</TableCell>
              <TableCell centerTitle>
                {item?.num_instances.toLocaleString()}
              </TableCell>
              <TableCell>{formatCurrency(item?.total_cost)}</TableCell>
              <TableCell>{formatPercent(item?.cost_percentage)}</TableCell>
              <TableCell align="center">
                <InfoOutlinedIcon
                  className={classes.superAdminIcon}
                  onClick={() => openDetailsModal(item)}
                />
              </TableCell>
            </TableRow>
          )}
          data={flavorsCostFields}
          onChangePage={handlePageChange}
          onChangeLimit={() => null}
          isLoading={isLoading}
          isFixed
          fillTable={flavorsCostFields?.results?.length === 1}
          notFoundText={i18n.t('COSTS_PAGE.NO_RESULTS')}
          overrideRowsCount={10}
          notRowsPerPage
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  monitor: { flavorsCostFields, flavorsCostPeriodFilter, flavorsCostIsLoading },
}: ApplicationState) => ({
  flavorsCostFields,
  intervalTime: flavorsCostPeriodFilter,
  isLoading: flavorsCostIsLoading,
});

export default connect(mapStateToProps, {
  setFlavorsCostPeriodFilter,
  getFlavorsCostRequest,
  modalClose,
  modalOpen,
})(FlavorsCostTable);
